import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import PhotoUpload from "@/components/PhotoUpload";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLES = ["Commissioner", "Deputy Commissioner", "Referee", "Umpire", "Scorekeeper", "Statistician", "Technical Official"];
const EMPTY = { name: "", role: "Referee", title: "", bio: "", photo_url: "" };

export default function OfficialsTab() {
    const [officials, setOfficials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = () =>
        api.get("/officials")
            .then((r) => setOfficials(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    useEffect(() => {
        load();
    }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY);
        setOpen(true);
    };
    const openEdit = (o) => {
        setEditing(o);
        setForm({ name: o.name, role: o.role || "Referee", title: o.title || "", bio: o.bio || "", photo_url: o.photo_url || "" });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editing) await api.put(`/officials/${editing.id}`, form);
            else await api.post("/officials", form);
            toast.success(editing ? "Official updated" : "Official added — epic portrait generates shortly");
            setOpen(false);
            load();
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (o) => {
        if (!window.confirm(`Remove ${o.name} from the officiating crew?`)) return;
        try {
            await api.delete(`/officials/${o.id}`);
            toast.success("Official removed");
            load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    const field = (key, label, placeholder = "") => (
        <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</Label>
            <Input
                data-testid={`official-form-${key}`}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                placeholder={placeholder}
                className="mt-2 bg-[#08080C] border-[#262638] text-white"
                required={key === "name"}
            />
        </div>
    );

    return (
        <div data-testid="admin-officials-tab">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <h2 className="font-display font-bold uppercase text-xl text-white">Officials ({officials.length})</h2>
                <button
                    data-testid="add-official-button"
                    onClick={openCreate}
                    className="flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] text-white font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors duration-200"
                >
                    <Plus className="w-4 h-4" /> Add Official
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin" /> Loading crew…
                </div>
            ) : officials.length === 0 ? (
                <div className="card-arena rounded-3xl p-12 text-center" data-testid="admin-officials-empty">
                    <ShieldCheck className="w-10 h-10 text-[#FF5500] mx-auto mb-3 opacity-60" />
                    <p className="font-display font-bold uppercase text-white">No officials yet</p>
                    <p className="text-sm text-slate-400 mt-1.5">Add your first official — their photo becomes an epic arena portrait automatically.</p>
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {officials.map((o) => (
                        <div key={o.id} data-testid={`admin-official-row-${o.id}`} className="card-arena rounded-2xl p-4 flex items-center gap-3">
                            {o.photo_url ? (
                                <img src={o.photo_url} alt={o.name} className="w-12 h-12 rounded-xl object-cover object-top border border-[#262638] shrink-0" />
                            ) : (
                                <div className="w-12 h-12 rounded-xl bg-[#1A1A28] border border-[#262638] flex items-center justify-center shrink-0">
                                    <ShieldCheck className="w-5 h-5 text-slate-500" />
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="font-display font-bold uppercase text-white truncate">{o.name}</p>
                                <p className="text-[10px] text-slate-500 uppercase tracking-widest truncate">
                                    {o.role}
                                    {o.title ? ` • “${o.title}”` : ""}
                                </p>
                            </div>
                            <button
                                data-testid={`edit-official-${o.id}`}
                                onClick={() => openEdit(o)}
                                className="p-2 text-slate-400 hover:text-[#FFB800] transition-colors duration-200"
                                aria-label={`Edit ${o.name}`}
                            >
                                <Pencil className="w-4 h-4" />
                            </button>
                            <button
                                data-testid={`delete-official-${o.id}`}
                                onClick={() => remove(o)}
                                className="p-2 text-slate-400 hover:text-[#EF4444] transition-colors duration-200"
                                aria-label={`Remove ${o.name}`}
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-[#12121A] border-[#262638] text-white max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black uppercase text-2xl">
                            {editing ? "Edit Official" : "Add Official"}
                        </DialogTitle>
                        <DialogDescription className="sr-only">Official details form</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submit} data-testid="official-form" className="space-y-4 mt-2">
                        {field("name", "Full Name", "e.g. Alex Navarro")}
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Role</Label>
                            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                <SelectTrigger data-testid="official-form-role" className="mt-2 bg-[#08080C] border-[#262638] text-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#12121A] border-[#262638] text-white">
                                    {ROLES.map((r) => (
                                        <SelectItem key={r} value={r}>
                                            {r}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {field("title", "Court Moniker (optional)", 'e.g. "The Enforcer"')}
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Bio (optional)</Label>
                            <textarea
                                data-testid="official-form-bio"
                                value={form.bio}
                                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                                rows={2}
                                placeholder="10 years calling the toughest games in Yellowknife…"
                                className="mt-2 w-full rounded-md bg-[#08080C] border border-[#262638] text-white px-3 py-2 text-sm focus:border-[#FF5500] focus:outline-none"
                            />
                        </div>
                        <PhotoUpload
                            label="Official Photo"
                            testid="official-form-photo"
                            value={form.photo_url}
                            onChange={(url) => setForm({ ...form, photo_url: url })}
                        />
                        <button
                            type="submit"
                            data-testid="official-form-submit"
                            disabled={saving}
                            className="w-full flex items-center justify-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-50 text-white font-display font-bold uppercase tracking-widest text-sm py-3 rounded-full transition-colors duration-200"
                        >
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                            {editing ? "Save Changes" : "Add Official"}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
